// Centralized error logging and monitoring system
import { supabase } from "@/integrations/supabase/client";
import { logError } from "./errorHandling";

export interface ErrorLog {
  id?: string;
  user_id?: string;
  error_message: string;
  error_stack?: string;
  page_url: string;
  user_agent: string;
  timestamp: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  metadata?: Record<string, any>;
}

export interface AnalyticsEvent {
  event_name: string;
  user_id?: string;
  page_url: string;
  timestamp: string;
  properties: Record<string, any>;
  session_id: string;
}

class MonitoringService {
  private sessionId: string;
  private errorQueue: ErrorLog[] = [];
  private analyticsQueue: AnalyticsEvent[] = [];
  private isOnline = navigator.onLine;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.setupGlobalErrorHandlers();
    this.setupOnlineOfflineHandlers();
    this.startPeriodicFlush();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private setupGlobalErrorHandlers() {
    // Global JavaScript error handler
    window.addEventListener('error', (event) => {
      this.logError({
        error_message: event.message,
        error_stack: event.error?.stack,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        severity: 'high',
        category: 'javascript_error',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        error_message: event.reason?.message || 'Unhandled promise rejection',
        error_stack: event.reason?.stack,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        severity: 'high',
        category: 'promise_rejection',
        metadata: {
          reason: event.reason,
        },
      });
    });

    // React error boundary integration (store reference for global access)
    (window as any).__MONITORING_SERVICE__ = this;
  }

  private setupOnlineOfflineHandlers() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.flushQueues();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  private startPeriodicFlush() {
    // Flush queues every 30 seconds
    setInterval(() => {
      if (this.isOnline) {
        this.flushQueues();
      }
    }, 30000);

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      this.flushQueues();
    });
  }

  // Error logging
  logError(errorLog: Omit<ErrorLog, 'user_id'>) {
    const userId = this.getCurrentUserId();
    const fullErrorLog: ErrorLog = {
      ...errorLog,
      user_id: userId,
    };

    this.errorQueue.push(fullErrorLog);

    // Also use existing error handling
    logError(new Error(errorLog.error_message), errorLog.metadata);

    // If critical error, try to flush immediately
    if (errorLog.severity === 'critical' && this.isOnline) {
      this.flushQueues();
    }
  }

  // Analytics event tracking
  track(eventName: string, properties: Record<string, any> = {}) {
    const userId = this.getCurrentUserId();
    const event: AnalyticsEvent = {
      event_name: eventName,
      user_id: userId,
      page_url: window.location.href,
      timestamp: new Date().toISOString(),
      properties: {
        ...properties,
        user_agent: navigator.userAgent,
        screen_resolution: `${screen.width}x${screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
      },
      session_id: this.sessionId,
    };

    this.analyticsQueue.push(event);

    // Console log for development
    if (import.meta.env.DEV) {
      console.log('Analytics Event:', eventName, properties);
    }
  }

  // Performance monitoring
  trackPageLoad() {
    if (typeof window.performance !== 'undefined') {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      
      this.track('page_load', {
        load_time: navigation.loadEventEnd - navigation.loadEventStart,
        dom_content_loaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
        first_paint: performance.getEntriesByName('first-paint')[0]?.startTime,
        first_contentful_paint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime,
        page_url: window.location.href,
      });
    }
  }

  // Core Web Vitals tracking
  trackWebVitals() {
    // Track Largest Contentful Paint (LCP)
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      
      this.track('web_vital_lcp', {
        value: lastEntry.startTime,
        metric: 'largest_contentful_paint',
      });
    });

    observer.observe({ entryTypes: ['largest-contentful-paint'] });

    // Track Cumulative Layout Shift (CLS)
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          clsValue += (entry as any).value;
        }
      }
      
      this.track('web_vital_cls', {
        value: clsValue,
        metric: 'cumulative_layout_shift',
      });
    });

    clsObserver.observe({ entryTypes: ['layout-shift'] });

    // Track First Input Delay (FID)
    const fidObserver = new PerformanceObserver((list) => {
      const firstEntry = list.getEntries()[0];
      
      this.track('web_vital_fid', {
        value: (firstEntry as any).processingStart - firstEntry.startTime,
        metric: 'first_input_delay',
      });
    });

    fidObserver.observe({ entryTypes: ['first-input'] });
  }

  private getCurrentUserId(): string | undefined {
    try {
      const authData = localStorage.getItem('sb-kllsxgvvwosxlcpxqwin-auth-token');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.user?.id;
      }
    } catch {
      // Silent fail
    }
    return undefined;
  }

  private async flushQueues() {
    if (this.errorQueue.length > 0) {
      await this.flushErrorQueue();
    }

    if (this.analyticsQueue.length > 0) {
      await this.flushAnalyticsQueue();
    }
  }

  private async flushErrorQueue() {
    const errors = [...this.errorQueue];
    this.errorQueue = [];

    try {
      // In a real application, you'd send this to your monitoring service
      // For now, we'll store in a local table or external service
      console.log('Flushing error logs:', errors);
      
      // Could integrate with Sentry, LogRocket, or other services here
      // await fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errors),
      // });
    } catch (error) {
      // Re-queue errors if flush failed
      this.errorQueue.unshift(...errors);
      console.error('Failed to flush error queue:', error);
    }
  }

  private async flushAnalyticsQueue() {
    const events = [...this.analyticsQueue];
    this.analyticsQueue = [];

    try {
      // In a real application, you'd send this to your analytics service
      console.log('Flushing analytics events:', events);
      
      // Could integrate with Google Analytics, Mixpanel, or other services here
      // await fetch('/api/analytics', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(events),
      // });
    } catch (error) {
      // Re-queue events if flush failed
      this.analyticsQueue.unshift(...events);
      console.error('Failed to flush analytics queue:', error);
    }
  }

  // User action audit logging (stored in user_actions table, separate from server-side audit_logs)
  async logUserAction(action: string, details: Record<string, any> = {}) {
    const userId = this.getCurrentUserId();
    if (!userId) return;

    try {
      await supabase.from('user_actions').insert({
        user_id: userId,
        action,
        details: {
          ...details,
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        },
        session_id: this.sessionId,
        page_url: window.location.href,
      });
    } catch (error) {
      console.error('Failed to log user action:', error);
    }
  }

  // Health check monitoring
  async performHealthCheck() {
    const healthData = {
      timestamp: new Date().toISOString(),
      supabase_connection: false,
      local_storage: false,
      network_status: navigator.onLine,
      memory_usage: (performance as any).memory ? {
        used: (performance as any).memory.usedJSHeapSize,
        total: (performance as any).memory.totalJSHeapSize,
        limit: (performance as any).memory.jsHeapSizeLimit,
      } : null,
    };

    try {
      // Test Supabase connection
      const { error } = await supabase.from('profiles').select('id').limit(1);
      healthData.supabase_connection = !error;
    } catch {
      healthData.supabase_connection = false;
    }

    try {
      // Test local storage
      localStorage.setItem('health_check', 'test');
      localStorage.removeItem('health_check');
      healthData.local_storage = true;
    } catch {
      healthData.local_storage = false;
    }

    this.track('health_check', healthData);
    return healthData;
  }
}

// Global monitoring instance
export const monitoring = new MonitoringService();

// React Error Boundary integration
export const logReactError = (error: Error, errorInfo: any) => {
  monitoring.logError({
    error_message: error.message,
    error_stack: error.stack,
    page_url: window.location.href,
    user_agent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    severity: 'high',
    category: 'react_error',
    metadata: {
      componentStack: errorInfo.componentStack,
    },
  });
};

// Convenience functions
export const trackPageView = (pageName?: string) => {
  monitoring.track('page_view', {
    page_name: pageName || document.title,
    referrer: document.referrer,
  });
};

export const trackUserAction = (action: string, target?: string, details?: Record<string, any>) => {
  monitoring.track('user_action', {
    action,
    target,
    ...details,
  });
};

export const trackError = (error: Error, context?: Record<string, any>) => {
  monitoring.logError({
    error_message: error.message,
    error_stack: error.stack,
    page_url: window.location.href,
    user_agent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    severity: 'medium',
    category: 'application_error',
    metadata: context,
  });
};

export const trackPerformance = (metricName: string, value: number, unit = 'ms') => {
  monitoring.track('performance_metric', {
    metric_name: metricName,
    value,
    unit,
  });
};

export default monitoring;